import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useLocale from '../../hooks/useLocale';

const PULSE_DURATION_MS = 700;

const pulse = keyframes`
  0%   { transform: scale(1);    box-shadow: 0 0 0 rgba(239,108,0,0); }
  50%  { transform: scale(1.08); box-shadow: 0 0 18px rgba(239,108,0,0.6); }
  100% { transform: scale(1.05); box-shadow: 0 0 12px rgba(239,108,0,0.4); }
`;

const Container = styled.section`
  max-width: 720px;
  margin: 0 auto;
  text-align: center;
`;

const Header = styled.header`
  margin-top: ${p => p.theme.spacing[4]}px;
  margin-bottom: ${p => p.theme.spacing[5]}px;
`;

const HeaderTitle = styled.h2`
  font-family: 'Noto Sans Tamil', 'Playfair Display', Georgia, serif;
  font-size: 20px;
  color: #8B3A00;
  margin: 0 0 ${p => p.theme.spacing[2]}px;
`;

const HeaderPrompt = styled.p`
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
  font-size: 14px;
  color: ${p => p.theme.colors.text.secondary};
  margin: 0;
  line-height: 1.6;
`;

const GridBox = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 5px;
  max-width: 560px;
  margin: 0 auto;
  aspect-ratio: 1 / 1;

  @media (max-width: ${p => p.theme.breakpoints.tablet}px) {
    max-width: 100%;
    gap: 4px;
  }

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    gap: 3px;
  }
`;

const Cell = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  padding: 0;
  cursor: ${p => p.disabled ? 'default' : 'pointer'};
  border-radius: 6px;
  transition: all 0.25s ease;
  font-family: 'Noto Sans Devanagari', serif;
  min-height: 44px;
  background: #FFF4E5;
  border: 1px solid rgba(239,108,0,0.25);

  &:hover:not(:disabled) {
    border-color: #EF6C00;
    transform: scale(1.04);
    box-shadow: 0 2px 8px rgba(239,108,0,0.2);
  }

  &:focus-visible {
    outline: 2px solid #EF6C00;
    outline-offset: 2px;
  }

  ${p => p.selected && css`
    background: linear-gradient(135deg, #FFD9B3, #FFB877);
    border: 2px solid #D84315;
    animation: ${pulse} ${PULSE_DURATION_MS}ms ease forwards;
  `}

  ${p => p.selectedReducedMotion && css`
    background: linear-gradient(135deg, #FFD9B3, #FFB877);
    border: 2px solid #D84315;
    transform: scale(1.05);
  `}
`;

const CellNumber = styled.span`
  font-size: 9px;
  color: #9B6B3A;
  font-family: ${p => p.theme.typography.monoFamily};
  margin-bottom: 2px;
`;

const DevChar = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: #5D2A00;
  line-height: 1;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    font-size: 15px;
  }
`;

const TamilChar = styled.span`
  font-size: 10px;
  color: #9B6B3A;
  font-family: 'Noto Sans Tamil', sans-serif;
  margin-top: 2px;
  line-height: 1;
`;

const LiveRegion = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
`;

function prefersReducedMotion() {
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

const GaneshaGrid = ({ data, onCellSelect, lang }) => {
  const t = useLocale(lang, 'ganeshaPrashnavali');
  const [selectedCell, setSelectedCell] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const timeoutRef = useRef(null);

  const handleCellTap = useCallback((cellIndex) => {
    if (isAnimating) return;
    setSelectedCell(cellIndex);
    setIsAnimating(true);

    const rm = prefersReducedMotion();
    setReducedMotion(rm);

    if (rm) {
      onCellSelect(cellIndex);
      return;
    }
    timeoutRef.current = setTimeout(() => {
      onCellSelect(cellIndex);
    }, PULSE_DURATION_MS);
  }, [isAnimating, onCellSelect]);

  useEffect(() => {
    const t = timeoutRef.current;
    return () => {
      if (t) clearTimeout(t);
    };
  }, []);

  const cells = data.responses;

  return (
    <Container>
      <Header>
        <HeaderTitle>{t.gridTitle || "Sri Ganesha's Guidance"}</HeaderTitle>
        <HeaderPrompt>{t.gridPrompt || 'Hold your question in mind, and tap any cell'}</HeaderPrompt>
      </Header>

      <LiveRegion aria-live="polite" />

      <GridBox role="grid" aria-label="Ganesha Prashnavali 8 by 8 grid">
        {cells.map(r => {
          const devanagari = r.syllable;
          const tamil = lang === 'ta' ? r.syllable_ta : r.syllable_ta;
          const isSelected = selectedCell === r.cell;
          const label = lang === 'ta'
            ? `கட்டம் ${r.cell}, ${r.syllable_ta}`
            : `Cell ${r.cell}, ${r.syllable}`;
          return (
            <Cell
              key={r.cell}
              role="gridcell"
              aria-label={label}
              selected={isSelected && !reducedMotion}
              selectedReducedMotion={isSelected && reducedMotion}
              onClick={() => handleCellTap(r.cell)}
              disabled={isAnimating}
              tabIndex={isAnimating ? -1 : 0}
            >
              <CellNumber>{r.cell}</CellNumber>
              <DevChar>{devanagari}</DevChar>
              <TamilChar>{tamil}</TamilChar>
            </Cell>
          );
        })}
      </GridBox>
    </Container>
  );
};

export default GaneshaGrid;
